'use client';

import { motion, useReducedMotion } from 'motion/react';
import { type ReactNode } from 'react';

// Figma "카드 등장 모션"의 스프링(감쇠 진동) 이징 — 페이드/상승에 적용
const SPRING_EASE = (t: number) =>
  1 -
  Math.exp(-t * 7.6657) *
    (Math.cos(t * 6.7605) + 1.1339 * Math.sin(t * 6.7605));

const APPEAR_DURATION = 1.1; // 페이드 + 아래→위(스프링)
const FLIP_DURATION = 0.8; // 가로 플립(scaleX 1→0→1)
const STAGGER = 0.24; // 카드 간 등장 간격(초)

// 양옆 카드의 스크롤 진입 등장: 페이드 + 아래→위(스프링) + 가로 플립.
// - run: 이 카드가 등장 애니메이션 대상인지(부모가 초기 집합 기준으로 명시 결정). 카드 수명 내 불변.
// - entered: 섹션이 뷰포트에 들어와 등장을 시작할지
// - slot: 스태거 순서(0,1,2,…). 등장 후 플립이 이어진다.
// run이 false면(중앙·화면 밖·이미 등장 후 진입한 카드) 래핑 없이 rest 상태로 즉시 표시한다.
// run은 카드별로 고정이라 엘리먼트 타입이 안정적 → 캐러셀 이동 시 리마운트되지 않는다.
export function CardEntrance({
  run,
  entered,
  slot,
  children,
}: {
  run: boolean;
  entered: boolean;
  slot: number;
  children: ReactNode;
}) {
  const prefersReducedMotion = useReducedMotion();

  if (!run || prefersReducedMotion) return <>{children}</>;

  const delay = slot * STAGGER;

  return (
    <motion.div
      // 등장 전(투명 상태)엔 포커스/상호작용 차단 → 안 보이는 요소로 포커스가 가지 않게 함(WCAG 2.4.7)
      inert={!entered}
      initial={{ opacity: 0, scaleX: 1, y: 55 }}
      animate={entered ? { opacity: 1, scaleX: [1, 0, 1], y: 0 } : undefined}
      transition={{
        opacity: { duration: APPEAR_DURATION, ease: SPRING_EASE, delay },
        y: { duration: APPEAR_DURATION, ease: SPRING_EASE, delay },
        // 등장이 끝난 뒤 가로 플립
        scaleX: {
          duration: FLIP_DURATION,
          ease: 'easeInOut',
          delay: delay + APPEAR_DURATION,
        },
      }}
    >
      {children}
    </motion.div>
  );
}
